import React, { useState, useEffect, useRef } from 'react';
import { auth, functions } from '../firebase';
import { signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import { AuthorDashboardProvider, useAuthorDashboard } from './AuthorDashboardContext';
import { getAccentClasses } from '../lib/themaCodes';
import AuthorOverviewTab from './tabs/AuthorOverviewTab';
import AuthorBooksTab from './tabs/AuthorBooksTab';
import AuthorCampaignsTab from './tabs/AuthorCampaignsTab';
import AuthorReadersTab from './tabs/AuthorReadersTab';
import AuthorArcRequestsTab from './tabs/AuthorArcRequestsTab';
import AuthorMessagesTab from './tabs/AuthorMessagesTab';
import AuthorReviewsTab from './tabs/AuthorReviewsTab';
import AuthorAnalyticsTab from './tabs/AuthorAnalyticsTab';
import AuthorSettingsTab from './tabs/AuthorSettingsTab';
import AuthorAudiobooksTab from './tabs/AuthorAudiobooksTab';
import AuthorEbooksTab from './tabs/AuthorEbooksTab';
import AuthorOrdersTab from './tabs/AuthorOrdersTab';
import AuthorAnnouncementsTab from './tabs/AuthorAnnouncementsTab';
import AuthorMailingListTab from './tabs/AuthorMailingListTab';
import AuthorSubscribersTab from './tabs/AuthorSubscribersTab';
import AuthorBookClubsTab from './tabs/AuthorBookClubsTab';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  LayoutDashboard, BookOpen, Megaphone, Users, Star, MessageCircle,
  BarChart3, Settings, LogOut, ChevronLeft, ChevronRight, User, AlertTriangle, Mail,
  Headphones, BookText, ShoppingCart, Bell, UsersRound, Inbox, MailCheck
} from 'lucide-react';

const NAV_ITEMS = [
  { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'books', label: 'My Books', icon: BookOpen },
  { id: 'campaigns', label: 'ARC Campaigns', icon: Megaphone },
  { id: 'arc-requests', label: 'ARC Requests', icon: Inbox },
  { id: 'readers', label: 'Readers', icon: Users },
  { id: 'mailing-list', label: 'Mailing List', icon: Mail },
  { id: 'subscribers', label: 'Subscribers', icon: MailCheck },
  { id: 'book-clubs', label: 'Book Clubs', icon: UsersRound },
  { id: 'reviews', label: 'Reviews', icon: Star },
  { id: 'messages', label: 'Messages', icon: MessageCircle },
  { id: 'announcements', label: 'Announcements', icon: Bell },
  { id: 'audiobooks', label: 'Audiobooks', icon: Headphones },
  { id: 'ebooks', label: 'eBooks', icon: BookText },
  { id: 'orders', label: 'Orders', icon: ShoppingCart },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'settings', label: 'Settings', icon: Settings },
];

// Error boundary for individual tabs — prevents one broken tab from crashing the whole dashboard
class TabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('AuthorDashboard tab error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-red-400" />
          <h3 className="mb-1 text-lg font-semibold text-red-800">Something went wrong</h3>
          <p className="mb-4 text-sm text-red-600">
            {this.state.error?.message || 'This section encountered an error.'}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => this.setState({ hasError: false, error: null })}
            className="border-red-300 text-red-700 hover:bg-red-100"
          >
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

function DashboardContent() {
  const { authorProfile, branding, isLoading, loadError, messageUnreadCount } = useAuthorDashboard();
  const { isReader: hasReaderRole, emailVerified, isSuperadmin, forceRefreshClaims, authorId } = useAuth();

  // A paying toolkit customer is not necessarily an ARC author: buying a plan
  // mints a LICENCE, not an author profile (createAuthor only runs from
  // AuthorSignup). Such an account has no `authorId` claim, and every item in
  // NAV_ITEMS is an ARC surface that would be empty and meaningless to them.
  //
  // RULE (Julie, 2026-07-29): licence-holders never see ARC or reader surfaces.
  // With no author identity the shell renders the hub alone — no ARC nav, no
  // reader-portal link, no ARC onboarding. Full ARC authors are unaffected.
  const hasAuthorIdentity = !!authorId;
  const navItems = hasAuthorIdentity ? NAV_ITEMS : [];
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [messageReaderId, setMessageReaderId] = useState(null);
  const rolesFixAttempted = useRef(false);

  // Auto-fix roles on first load if they look incomplete
  useEffect(() => {
    if (rolesFixAttempted.current) return;
    rolesFixAttempted.current = true;
    const fixRoles = httpsCallable(functions, 'fixAuthorRoles');
    fixRoles({}).then(async (result) => {
      console.log('fixAuthorRoles result:', result.data);
      // Refresh claims so the UI picks up any new roles
      await forceRefreshClaims();
    }).catch((err) => {
      console.warn('fixAuthorRoles failed (non-blocking):', err.message);
    });
  }, [forceRefreshClaims]);

  const accent = getAccentClasses(branding?.accentColor || 'teal');

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />
          <p className="text-sm text-slate-500">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  function renderTab() {
    switch (activeTab) {
      case 'overview': return <AuthorOverviewTab onNavigate={setActiveTab} />;
      case 'books': return <AuthorBooksTab />;
      case 'campaigns': return <AuthorCampaignsTab />;
      case 'arc-requests': return <AuthorArcRequestsTab />;
      case 'readers': return <AuthorReadersTab onMessageReader={(readerId) => { setMessageReaderId(readerId); setActiveTab('messages'); }} />;
      case 'mailing-list': return <AuthorMailingListTab />;
      case 'subscribers': return <AuthorSubscribersTab />;
      case 'book-clubs': return <AuthorBookClubsTab />;
      case 'reviews': return <AuthorReviewsTab />;
      case 'messages': return <AuthorMessagesTab initialReaderId={messageReaderId} key={messageReaderId || 'messages'} />;
      case 'announcements': return <AuthorAnnouncementsTab />;
      case 'audiobooks': return <AuthorAudiobooksTab />;
      case 'ebooks': return <AuthorEbooksTab />;
      case 'orders': return <AuthorOrdersTab />;
      case 'analytics': return <AuthorAnalyticsTab />;
      case 'settings': return <AuthorSettingsTab />;
      default: return <AuthorOverviewTab onNavigate={setActiveTab} />;
    }
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 z-30 flex h-full flex-col border-r border-slate-200 bg-white transition-all ${
        sidebarCollapsed ? 'w-16' : 'w-56'
      }`}>
        {/* Logo / Author info */}
        <div className="border-b border-slate-100 p-3">
          {!sidebarCollapsed ? (
            <div className="flex items-center gap-3">
              {branding?.logo ? (
                <img src={branding.logo} alt="" className="h-9 w-9 rounded-lg object-cover" />
              ) : (
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent.bg} text-white font-bold`}>
                  {(authorProfile?.penName || authorProfile?.name || 'A').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">
                  {branding?.displayName || authorProfile?.penName || authorProfile?.name || 'Dashboard'}
                </p>
                <p className="text-[10px] text-slate-400">Powered by Narratoria</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              {branding?.logo ? (
                <img src={branding.logo} alt="" className="h-8 w-8 rounded-lg object-cover" />
              ) : (
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${accent.bg} text-white font-bold text-sm`}>
                  {(authorProfile?.penName || authorProfile?.name || 'A').charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 space-y-0.5 p-2 overflow-y-auto">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            const badge = item.id === 'messages' ? messageUnreadCount : 0;
            return (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); if (item.id !== 'messages') setMessageReaderId(null); }}
                className={`relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? `${accent.bg} text-white`
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                } ${sidebarCollapsed ? 'justify-center' : ''}`}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {!sidebarCollapsed && <span>{item.label}</span>}
                {badge > 0 && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Bottom actions */}
        <div className="border-t border-slate-100 p-2">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            {!sidebarCollapsed && <span>Collapse</span>}
          </button>
          {hasReaderRole && hasAuthorIdentity && (
            <button
              onClick={() => { window.location.href = '/reader'; }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-teal-50 hover:text-teal-600"
              title={sidebarCollapsed ? 'Reader Portal' : undefined}
            >
              <User className="h-4 w-4" />
              {!sidebarCollapsed && <span>Reader Portal</span>}
            </button>
          )}
          <button
            onClick={() => signOut(auth).then(() => { window.location.href = '/author/login'; })}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-red-50 hover:text-red-600"
          >
            <LogOut className="h-4 w-4" />
            {!sidebarCollapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className={`flex-1 transition-all ${sidebarCollapsed ? 'ml-16' : 'ml-56'}`}>
        <div className="mx-auto max-w-6xl p-6">
          {!emailVerified && !isSuperadmin && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-800">
              <Mail className="h-4 w-4 flex-shrink-0" />
              <span>
                Please verify your email to unlock ARC campaigns.{' '}
                <a href="/author/verify-email" className="font-medium underline hover:text-teal-900">
                  Verify now
                </a>
              </span>
            </div>
          )}
          {loadError && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{loadError} — some data may be missing. Try refreshing the page.</span>
            </div>
          )}
          <TabErrorBoundary key={activeTab}>
            {renderTab()}
          </TabErrorBoundary>
        </div>
      </main>
    </div>
  );
}

export default function AuthorDashboard() {
  const { user, isLoading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />
      </div>
    );
  }

  if (!user) {
    window.location.href = '/author/login';
    return null;
  }

  return (
    <AuthorDashboardProvider>
      <DashboardContent />
    </AuthorDashboardProvider>
  );
}
